import { LegalPage } from "../../components/LegalPage";

export function SmsTerms() {
  return (
    <LegalPage
      title="SMS Terms & Opt-In Disclosure"
      description="WeConsult Rx SMS messaging program terms: opt-in, message frequency, opt-out, message and data rates, and Twilio A2P 10DLC + TCPA compliance disclosures."
      path="/sms-terms"
      effective="2026-05-30"
    >
      <p>
        This page describes the WeConsult Rx SMS (text message) program,
        including how you opt in, what messages you can expect, how to opt out,
        and required Twilio A2P 10DLC and US TCPA (Telephone Consumer Protection
        Act) disclosures. By providing your mobile phone number and opting in,
        you agree to these SMS Terms.
      </p>

      <h2>Program description</h2>
      <p>
        <strong>Program name:</strong> WeConsult Rx Notifications
        <br />
        <strong>Brand:</strong> WeConsult Rx, operated by DRX Consulting Group,
        LLC.
        <br />
        <strong>Use cases:</strong> account, transactional, and operational
        messages — including (a) appointment / review reminders for consultant
        pharmacist workflows, (b) license renewal and lab-due reminders for
        facility staff, (c) report bundle signing confirmations, (d) two-factor
        authentication (2FA) codes, and (e) one-time-password (OTP)
        verification.
      </p>
      <p>
        We do <strong>not</strong> use this SMS program for promotional or
        marketing messages. Marketing communications, if any, are sent only by
        email and only with separate, explicit consent.
      </p>

      <h2>How to opt in</h2>
      <p>You can opt in to the WeConsult Rx SMS program in any of the following ways:</p>
      <ul>
        <li>
          <strong>Public opt-in form:</strong> visit{" "}
          <a href="/sms-opt-in">weconsultrx.com/sms-opt-in</a>, enter your
          mobile phone number, and check the consent box agreeing to receive
          SMS messages from WeConsult Rx for the purposes described in these
          SMS Terms. No account is required. The demo-request and contact forms
          at <a href="https://weconsultrx.com">weconsultrx.com</a> also carry
          the same optional SMS-consent checkbox.
        </li>
        <li>
          <strong>In-product:</strong> by enabling the SMS channel for one or
          more notification kinds inside the WeConsult Rx application at{" "}
          <a href="https://app.weconsultrx.com">app.weconsultrx.com</a> under
          Settings → Notifications. The opt-in is captured per user per kind
          per channel and stored in your account notification preferences.
        </li>
        <li>
          <strong>Text reply:</strong> by replying <code>START</code> or{" "}
          <code>YES</code> to any prior WeConsult Rx SMS message after opting
          out.
        </li>
      </ul>
      <p>
        Each opt-in is logged with a timestamp, the channel of consent, the IP
        address (where applicable), and the user identifier. We retain these
        records for the duration of the program plus seven (7) years to comply
        with TCPA recordkeeping expectations.
      </p>

      <h2>Sample messages</h2>
      <p>You may receive messages similar to the following:</p>
      <ul>
        <li>
          <em>
            “WeConsult Rx: Your monthly review bundle for Sunset Manor is ready
            for signature. View: https://app.weconsultrx.com/bundles/&lt;id&gt;
            Reply STOP to opt out.”
          </em>
        </li>
        <li>
          <em>
            “WeConsult Rx: Reminder — your state pharmacist license renewal
            expires in 30 days. Reply STOP to opt out.”
          </em>
        </li>
        <li>
          <em>
            “WeConsult Rx: Your verification code is 123456. It expires in 5
            minutes. Do not share this code.”
          </em>
        </li>
      </ul>

      <h2>Message frequency</h2>
      <p>
        Message frequency varies based on your facility’s activity and the
        notification kinds you enable. A typical user receives between 0 and 8
        messages per week. Verification (2FA / OTP) messages are sent only when
        you request them.
      </p>

      <h2>Message and data rates</h2>
      <p>
        <strong>Message and data rates may apply.</strong> WeConsult Rx does
        not charge for SMS messages, but your mobile carrier may charge you for
        messages sent to or from your device. Contact your carrier for details
        on your plan.
      </p>

      <h2>How to opt out</h2>
      <p>
        You can opt out of the WeConsult Rx SMS program at any time by:
      </p>
      <ul>
        <li>
          Replying <code>STOP</code>, <code>STOPALL</code>, <code>END</code>,{" "}
          <code>QUIT</code>, <code>CANCEL</code>, or <code>UNSUBSCRIBE</code>{" "}
          to any WeConsult Rx SMS message. You will receive a single
          confirmation that you have been unsubscribed, and you will not
          receive further SMS messages from the program until you opt back in.
        </li>
        <li>
          Toggling the SMS channel off in the WeConsult Rx application under
          Settings → Notifications.
        </li>
        <li>
          Emailing{" "}
          <a href="mailto:sms-optout@weconsultrx.com">
            sms-optout@weconsultrx.com
          </a>{" "}
          with the phone number(s) you wish to remove.
        </li>
      </ul>

      <h2>How to get help</h2>
      <p>
        Reply <code>HELP</code> to any WeConsult Rx SMS message to receive
        these contact details. You can also email{" "}
        <a href="mailto:support@weconsultrx.com">support@weconsultrx.com</a> or
        visit <a href="/contact">our contact page</a>.
      </p>

      <h2>Supported carriers</h2>
      <p>
        The WeConsult Rx SMS program is operated via Twilio, Inc., a CTIA- and
        carrier-approved A2P 10DLC messaging provider. Carriers — including
        AT&amp;T, Verizon, T-Mobile, US Cellular, Sprint, Boost, MetroPCS,
        Cricket, and most US MVNOs — are supported but are not responsible for
        delayed or undelivered messages.
      </p>

      <h2>Mobile information sharing — explicit non-disclosure</h2>
      <p>
        <strong>
          No mobile information, including phone numbers and SMS opt-in status,
          will be shared with third parties or affiliates for marketing or
          promotional purposes.
        </strong>{" "}
        Information sharing is limited to subprocessors — primarily Twilio for
        message delivery and Supabase for at-rest storage — strictly as
        required to operate this SMS program. See our{" "}
        <a href="/subprocessors">Subprocessors</a> page for the full list and
        our <a href="/privacy">Privacy Policy</a> for details.
      </p>

      <h2>Eligibility and age</h2>
      <p>
        You must be at least eighteen (18) years old, the authorized account
        holder for the mobile phone number you provide, and located in the
        United States to opt in. By opting in, you represent that you meet
        these conditions.
      </p>

      <h2>Protected Health Information (PHI)</h2>
      <p>
        WeConsult Rx SMS messages <strong>do not contain</strong> patient
        names, medical record numbers, dates of birth, diagnoses, medications,
        or other Protected Health Information (PHI) regulated under HIPAA. SMS
        templates are restricted to a strict variable whitelist that includes
        only facility-level, account-level, and resource-identifier (UUID)
        fields. See our <a href="/hipaa">HIPAA Compliance</a> page for the
        technical controls that enforce this gate at both prompt and rendering
        time.
      </p>

      <h2>Changes to these SMS Terms</h2>
      <p>
        We may update these SMS Terms from time to time. We will post the
        updated terms on this page with a new “Effective” date. Material changes
        will be communicated via in-product notice or email at least thirty
        (30) days in advance where practicable.
      </p>

      <h2>Contact</h2>
      <p>
        WeConsult Rx
        <br />
        DRX Consulting Group, LLC
        <br />
        Email:{" "}
        <a href="mailto:sms-support@weconsultrx.com">
          sms-support@weconsultrx.com
        </a>
        <br />
        General privacy contact:{" "}
        <a href="mailto:privacy@weconsultrx.com">privacy@weconsultrx.com</a>
      </p>

      <h2>Related policies</h2>
      <ul>
        <li>
          <a href="/privacy">Privacy Policy</a>
        </li>
        <li>
          <a href="/terms">Terms of Service</a>
        </li>
        <li>
          <a href="/hipaa">HIPAA Compliance Statement</a>
        </li>
        <li>
          <a href="/subprocessors">Subprocessors</a>
        </li>
      </ul>
    </LegalPage>
  );
}
